import React, { useCallback, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import HubmedPricingBlock from "@/components/admin/HubmedPricingBlock";

const HubMedPricingSettingsPage = () => {
  const [pricingModalOpen, setPricingModalOpen] = useState(false);

  const [phSearchTerm, setPhSearchTerm] = useState("");
  const [phSearchLoading, setPhSearchLoading] = useState(false);
  const [phSearchError, setPhSearchError] = useState(null);
  const [phChemists, setPhChemists] = useState([]);
  const [phHalaxyError, setPhHalaxyError] = useState(null);
  const [phSelected, setPhSelected] = useState(null);

  const resetPricingSection = useCallback(() => {
    setPhSearchTerm("");
    setPhSearchError(null);
    setPhChemists([]);
    setPhHalaxyError(null);
    setPhSelected(null);
    setPricingModalOpen(false);
  }, []);

  const runPhSearch = async () => {
    const q = phSearchTerm.trim();
    if (!q) {
      setPhSearchError("Enter part of the pharmacy or chemist name.");
      return;
    }
    setPhSearchLoading(true);
    setPhSearchError(null);
    setPhHalaxyError(null);
    setPhSelected(null);
    setPricingModalOpen(false);

    try {
      const data = await phappsRequest(
        API_PATHS.SEARCH_HALAXY_FOR_CHEMIST,
        "POST",
        { HaloSearch: q }
      );
      setPhChemists(Array.isArray(data.chemists) ? data.chemists : []);
      setPhHalaxyError(data.halaxyError ?? null);
    } catch (e) {
      setPhSearchError(e.message || "Search failed.");
      setPhChemists([]);
    } finally {
      setPhSearchLoading(false);
    }
  };

  const phChemistId = phSelected?.ChemistID ?? null;

  return (
    <div className="max-w-5xl mx-auto p-2 sm:p-4 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
          Pricing settings
        </h1>
      </div>

      <section className="rounded-2xl border border-slate-200/80 bg-white shadow-sm p-6">
        <div className="space-y-6">
          <p className="text-sm text-slate-600">
            Search by pharmacy or chemist name, select a chemist, then open the
            DocLink pricing editor.
          </p>

          <section className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                Search
              </h3>
              <button
                type="button"
                onClick={resetPricingSection}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
              >
                Clear search
              </button>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={phSearchTerm}
                onChange={(e) => setPhSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runPhSearch()}
                placeholder="e.g. Glenns or Thursday Island"
                className="flex-1 rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                disabled={phSearchLoading}
              />
              <button
                type="button"
                onClick={runPhSearch}
                disabled={phSearchLoading}
                className="rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50 disabled:pointer-events-none"
              >
                {phSearchLoading ? "Searching…" : "Search"}
              </button>
            </div>
            {phSearchError && (
              <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-600">
                {phSearchError}
              </p>
            )}
            {phHalaxyError && (
              <p className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Halaxy: {String(phHalaxyError)}
              </p>
            )}
          </section>

          <section className="flex min-h-[200px] flex-col rounded-xl border border-slate-200 p-4">
            <h3 className="mb-2 text-xs font-semibold text-teal-900">
              Pharmacy Halo chemists
            </h3>
            <ul className="max-h-72 flex-1 space-y-2 overflow-y-auto">
              {phChemists.length === 0 && (
                <li className="py-4 text-center text-sm text-slate-400">
                  No results yet. Search above.
                </li>
              )}
              {phChemists.map((c) => {
                const active = phSelected?.ChemistID === c.ChemistID;
                return (
                  <li key={c.ChemistID}>
                    <button
                      type="button"
                      onClick={() => setPhSelected(c)}
                      className={`w-full rounded-lg border px-3 py-2.5 text-left text-sm font-medium transition ${
                        active
                          ? "border-teal-500 bg-teal-50 text-teal-900"
                          : "border-slate-100 bg-white text-slate-800 hover:border-slate-200"
                      }`}
                    >
                      {c.ChemistName}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => setPricingModalOpen(true)}
              disabled={!phSelected}
              className="rounded-xl bg-gradient-to-r from-violet-600 to-teal-600 px-8 py-2.5 text-sm font-semibold text-white shadow-md disabled:pointer-events-none disabled:opacity-40"
            >
              Edit pricing…
            </button>
            {phSelected ? (
              <Typography variant="body2" color="text.secondary" className="text-center">
                Selected: <strong>{phSelected.ChemistName}</strong>
              </Typography>
            ) : null}
          </div>
        </div>
      </section>

      <Dialog
        open={pricingModalOpen}
        onClose={() => setPricingModalOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { maxHeight: "92vh", borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
          }}
        >
          <span>DocLink pricing</span>
          <IconButton
            aria-label="Close"
            onClick={() => setPricingModalOpen(false)}
            edge="end"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {phChemistId != null ? (
            <HubmedPricingBlock
              chemistId={phChemistId}
              title=""
              emptyHint=""
              zConfirm={1600}
            />
          ) : (
            <Typography color="text.secondary">
              Select a chemist first.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPricingModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default HubMedPricingSettingsPage;
